import type {
  AccountingLocation,
  AccountingTeam,
  AccountingToday,
  AccountingTargets,
  AccountingYear,
  AccountingYears,
  Expense,
  ExpenseCategory,
  ExpenseChange,
  ExpenseGroup,
  ExpenseRow,
  SalaryPerson,
  SaveExpense,
  SaveSalary,
  SetLastDay,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Accounting — profit, expenses and salaries (STORY-172, AC-D1–AC-D22).
 *
 * **`branches` is a comma-separated list of location ids** — the locations the
 * screen's chips have selected (AC-D6). The server checks every one against
 * what the caller may read, so the list is a filter and never a grant.
 *
 * Reading needs the part's own right — `accounting.view` for profit,
 * `accounting.expenses.view`, `accounting.salaries.view` (QA4-D8) — and writing
 * `accounting.manage` for expenses or `accounting.salaries.manage` for pay. The
 * server refuses anyone else, whatever the screen draws.
 */

/** The locations the caller may read accounts for — what the chips list. */
/**
 * The locations whose accounts — one part of them — the caller may read
 * (QA4-D8): profit, expenses or salaries, each its own right now.
 */
export function listAccountingLocations(
  signal?: AbortSignal,
  part: "profit" | "expenses" | "salaries" = "profit",
): Promise<AccountingLocation[]> {
  return api<AccountingLocation[]>(`/accounting/locations?part=${part}`, {
    branchScoped: true,
    signal,
  });
}

/** The clinic's today, in its own timezone — for the tabs that carry no profit (QA4-D8). */
export function getAccountingDay(signal?: AbortSignal): Promise<{ today: string }> {
  return api<{ today: string }>("/accounting/day", { branchScoped: true, signal });
}

/** Every year holding any money, for these locations. */
export function getAccountingYears(
  branches: string,
  signal?: AbortSignal,
): Promise<AccountingYears> {
  return api<AccountingYears>(`/accounting/years?${query({ branches })}`, {
    branchScoped: true,
    signal,
  });
}

/** One year: twelve months, and every day up to the clinic's today. */
export function getAccountingYear(
  year: number,
  branches: string,
  signal?: AbortSignal,
): Promise<AccountingYear> {
  return api<AccountingYear>(`/accounting/year?${query({ year: String(year), branches })}`, {
    branchScoped: true,
    signal,
  });
}

/* ── categories (AC-D4) ──────────────────────────────────────────────────── */

export function listExpenseCategories(signal?: AbortSignal): Promise<ExpenseCategory[]> {
  return api<ExpenseCategory[]>("/accounting/categories", { branchScoped: true, signal });
}

export function createExpenseCategory(name: string, group: ExpenseGroup): Promise<ExpenseCategory> {
  return api<ExpenseCategory>("/accounting/categories", {
    method: "POST",
    body: { name, group },
    branchScoped: true,
  });
}

export function renameExpenseCategory(
  categoryId: string,
  name: string,
  group: ExpenseGroup,
): Promise<ExpenseCategory> {
  return api<ExpenseCategory>(`/accounting/categories/${categoryId}`, {
    method: "PATCH",
    body: { name, group },
    branchScoped: true,
  });
}

/** Refused by the server while any expense is filed under it. */
export function removeExpenseCategory(categoryId: string): Promise<void> {
  return api<void>(`/accounting/categories/${categoryId}`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/** Providers and chairs for a year, or one month of it (`month` 0 is the whole year). */
export function getAccountingTeam(
  year: number,
  month: number,
  branches: string,
  signal?: AbortSignal,
): Promise<AccountingTeam> {
  const query = `year=${String(year)}&month=${String(month)}&branches=${encodeURIComponent(branches)}`;
  return api<AccountingTeam>(`/accounting/team?${query}`, { branchScoped: true, signal });
}

/** What is expected today, collected so far and still to come (DY-D1–DY-D6). */
export function getAccountingToday(
  branches: string,
  signal?: AbortSignal,
): Promise<AccountingToday> {
  return api<AccountingToday>(`/accounting/today?branches=${encodeURIComponent(branches)}`, {
    branchScoped: true,
    signal,
  });
}

/* ── targets (AF-D6) ─────────────────────────────────────────────────────── */

/** The clinic's targets in basis points, null for a measure nobody has set. */
export function getAccountingTargets(signal?: AbortSignal): Promise<AccountingTargets> {
  return api<AccountingTargets>("/accounting/targets", { branchScoped: true, signal });
}

/** Sets all three at once; null clears one. Needs `accounting.manage`. */
export function saveAccountingTargets(targets: AccountingTargets): Promise<AccountingTargets> {
  return api<AccountingTargets>("/accounting/targets", {
    method: "PUT",
    body: targets,
    branchScoped: true,
  });
}

/* ── expenses ────────────────────────────────────────────────────────────── */

/** Expenses paid between two days inclusive, newest first. */
export function listExpenses(
  from: string,
  to: string,
  branches: string,
  signal?: AbortSignal,
): Promise<ExpenseRow[]> {
  return api<ExpenseRow[]>(`/accounting/expenses?${query({ from, to, branches })}`, {
    branchScoped: true,
    signal,
  });
}

export function createExpense(body: SaveExpense): Promise<Expense> {
  return api<Expense>("/accounting/expenses", { method: "POST", body, branchScoped: true });
}

export function updateExpense(expenseId: string, body: Partial<SaveExpense>): Promise<Expense> {
  return api<Expense>(`/accounting/expenses/${expenseId}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

export function deleteExpense(expenseId: string): Promise<void> {
  return api<void>(`/accounting/expenses/${expenseId}`, { method: "DELETE", branchScoped: true });
}

/** Every change to one expense, from the audit log (AC-D21). */
export function listExpenseHistory(
  expenseId: string,
  signal?: AbortSignal,
): Promise<ExpenseChange[]> {
  return api<ExpenseChange[]>(`/accounting/expenses/${expenseId}/history`, {
    branchScoped: true,
    signal,
  });
}

/* ── salaries (AC-D3, AC-D10, AC-D18) ────────────────────────────────────── */

export function listSalaries(signal?: AbortSignal): Promise<SalaryPerson[]> {
  return api<SalaryPerson[]>("/accounting/salaries", { branchScoped: true, signal });
}

/** A new dated salary. Months before its date keep the old one (AC-D10). */
export function saveSalary(userId: string, body: SaveSalary): Promise<SalaryPerson> {
  return api<SalaryPerson>(`/accounting/salaries/${userId}`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/** Sets or clears (`endedOn: null`) a person's last day. */
export function setLastDay(userId: string, body: SetLastDay): Promise<SalaryPerson> {
  return api<SalaryPerson>(`/accounting/salaries/${userId}/last-day`, {
    method: "PUT",
    body,
    branchScoped: true,
  });
}

/** A query string, encoded — a location id is a UUID, but nothing here assumes it. */
function query(params: Record<string, string>): string {
  return new URLSearchParams(params).toString();
}
